import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Card,
    CardBody,
    Col,
    Container,
    Form,
    FormFeedback,
    Input,
    Label,
    Row,
} from "reactstrap";
import * as Yup from "yup";
import ToastDialog from "components/Common/ToastDialog";
import withRouter from "components/Common/withRouter";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import Select from "react-select";
import Loader from "react-spinner-loader";

import Breadcrumbs from "components/Common/BreadcrumbTitle";

import API_SERVICES from "constants/app.constants";
import { callGetGSTINDetails, callGetPanDetails } from "http/gstin/gstin";
import { useAsyncDebounce } from "react-table";
import {
    callGetCity,
    callGetState,
    getEntity,
    addPartner,
} from "../../http/fundLync/entityAndAddress.helper";
import { getRoleByEntityType } from "http/fundLync/role.helper";
import { decryptedValue } from "common/utility";

const AddPartner = (props) => {
    document.title = "Add Partner | Fundlync";

    const navigate = useNavigate();
    const [loader, setLoader] = useState(false);
    const [saveLoader, setSaveLoader] = useState(false);

    const [isOpen, setIsOpen] = useState(false);
    const [isAlready, setIsAlready] = useState(false);
    const [toastInfo, setToastInfo] = useState({ showToast: false });
    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);
    const currentDate = new Date().toISOString().split("T")[0];
    const [isConstitution, setIsConstitution] = useState(false);
    const [partnerType, setPartnerType] = useState("")
    const [roleList, setRoleList] = useState([])
    const user = decryptedValue(localStorage.getItem("user"));
    // validation
    const validation = useFormik({
        enableReinitialize: true,

        initialValues: {
            partnerType: "",
            gstnIn: "",
            pan: "",
            entityName: "",
            dateOfIncorporation: "",
            entityConstitution: "",
            cin: "",
            companyDescription: "",

            addressType: "",
            address1: "",
            address2: "",
            address3: "",
            state: "",
            city: "",
            pincode: "",

            firstName: "",
            lastName: "",
            email: "",
            mobileNo: "",
            roleId: ""
        },

        validationSchema: Yup.object({
            partnerType: Yup.string().required("Please Select Partner Type"),
            gstnIn: Yup.string(),
            pan: Yup.string()
                .matches(API_SERVICES.PAN_PATTERN, "Enter a valid PAN number")
                .required("PAN is required"),
            entityName: partnerType == "Entity"
                ? Yup.string().required("Please Enter Entity Name")
                : Yup.string(),
            dateOfIncorporation: partnerType == "Entity"
                ? Yup.string().required("Please Enter Date of Incorporation")
                : Yup.string(),
            entityConstitution: partnerType == "Entity"
                ? Yup.string().required("Please Enter Entity Constitution")
                : Yup.string(),
            cin: isConstitution && partnerType == "Entity"
                ? Yup.string().matches(API_SERVICES.CIN_PATTERN, "Enter a valid CIN number").required("Please Enter CIN")
                : Yup.string().matches(API_SERVICES.CIN_PATTERN, "Enter a valid CIN number"),
            companyDescription: Yup.string(),

            addressType: Yup.string().required("Address Type is Required"),
            address1: Yup.string().required("Please Enter Address line 1"),
            address2: Yup.string(),
            address3: Yup.string(),
            state: Yup.string().required("Please Enter State"),
            city: Yup.string().required("Please Enter City"),
            pincode: Yup.number()
                .required("Please Enter Pincode")
                .typeError("Pincode Must be a Number")
                .positive("Pincode must be a positive number")
                .integer("Pincode must be an integer")
                .test('len', 'Pincode must be exactly 6 digits', val => val && val.toString().length === 6),

            firstName: isOpen
                ? Yup.string().required("Please Enter First Name")
                : Yup.string(),
            lastName: isOpen
                ? Yup.string().required("Please Enter Last Name")
                : Yup.string(),
            email: isOpen
                ? Yup.string().required("Please Enter Email")
                : Yup.string(),
            mobileNo: isOpen
                ? Yup.string()
                    .required("Please Enter Mobile No")
                    .matches(/^\d{10}$/, "Mobile Number must be exactly 10 digits")
                : Yup.string(),
            roleId: isOpen
                ? Yup.string().required("Select role type")
                : Yup.string(),
        }),
        onSubmit: (values) => {
            const partner = {
                pan: values.pan,
                roleId: values.roleId,
                entityType: 'Partner',
                isVerify: true,

                addressType: values.addressType,
                address1: values.address1,
                city: values.city,
                state: values.state,
                pincode: values.pincode,

                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                mobileNo: String(values.mobileNo)
            };

            if (values.address2) {
                partner.address2 = values.address2;
            }
            
            if (values.address3) {
                partner.address3 = values.address3;
            }

            if (partnerType === "Entity") {
                partner['entityName'] = values.entityName;
                partner['gstnIn'] = values.gstnIn;
                partner['cin'] = values.cin;
                partner['entityConstitution'] = values.entityConstitution;
                partner['dateOfIncorporation'] = values.dateOfIncorporation;

                if (values.companyDescription) {
                    partner['companyDescription'] = values.companyDescription;
                }

            } else {
                partner['entityName'] = values.firstName+' '+values.lastName;
                partner['pan'] = values.pan;
                partner['entityConstitution'] = "Private Limited Company"
            }

            setSaveLoader(true)
            addPartner(partner)
                .then((response) => {
                    if ((response.code == 201 && response.status)) {
                        setSaveLoader(false)
                        setToastInfo({
                            message: "Entity Details Saved",
                            showToast: true,
                            role: "success",
                        });
                        navigate(`/${user.roleName}/listPartner`);
                    } else {
                        setSaveLoader(false)
                        const { message } = response;
                        setToastInfo({
                            message: message,
                            showToast: true,
                            role: "error",
                        });
                    }
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(f => {
                    setSaveLoader(false);
                })
        },
    });

    const handlClick = () => {
        let set = !isOpen;
        setIsOpen(set);
    };

    const onGSTINChange = useAsyncDebounce((value) => {
        findGSTINDetails(value);
    }, 400);

    const onPanChange = useAsyncDebounce((value) => {
        if (!validation.errors.pan) findPanDetails(value);
    }, 400);

    const findGSTINDetails = (gstin) => {
        setLoader(true);
        callGetGSTINDetails({ gstnIn: gstin })
            .then(async (result) => {
                if (result.code == 200 && result.status) {
                    if (await checkIfalreadyExist(gstin)) {
                        setIsAlready(true);
                        setToastInfo({
                            message: "GSTIN already registered",
                            showToast: true,
                            role: "error",
                        });
                    } else {
                        setIsAlready(false);
                        const { data: gstinDetails } = result;
                        const { tradeNam, ctb } = gstinDetails;
                        const { stcd, dst, pncd, bnm, flno, landMark } =
                            gstinDetails.pradr.addr;
                        validation.setFieldValue("entityName", tradeNam);
                        validation.setFieldValue("entityConstitution", ctb);
                        validation.setFieldValue("address1", bnm);
                        validation.setFieldValue("address2", flno);
                        validation.setFieldValue("address3", landMark);
                        validation.setFieldValue("pincode", pncd);
                        if (stateList && stcd) {
                            validation.setFieldValue("state", stcd.toUpperCase());
                            getCityList(stcd)
                        }
                        if (cityList && dst) {
                            validation.setFieldValue("city", dst.toUpperCase());
                        }

                        validation.setFieldValue("pan", gstin.substring(2, 12));
                    }
                } else {
                    setToastInfo({
                        message: "Incorrect GSTIN",
                        showToast: true,
                        role: "error",
                    });
                    validation.setFieldValue("entityName", "");
                    validation.setFieldValue("entityConstitution", "");
                }
            })
            .catch((e) => {
                setToastInfo({
                    message: "GSTIN not found",
                    showToast: true,
                    role: "error",
                });
                validation.setFieldValue("companyName", "");
                validation.setFieldValue("constitution", "");
            })
            .finally((f) => {
                setLoader(false);
            });
    };

    const checkIfalreadyExist = async (value) => {
        let status = await getEntity({ searchKey: value })
        return status.status
    }

    const findPanDetails = (pan) => {
        setLoader(true);
        callGetPanDetails({ pan: pan })
            .then((result) => {
                if (result.code == 200 && result.data.status !== "INVALID") {
                    const { data: panDetails } = result;
                    const { full_name, category } = panDetails;

                    if (partnerType === "Individual") {
                        const name = full_name.split(' ')
                        const last_name = name.length > 1 ? full_name.substring(name[0].length + 1) : ""
                        validation.setFieldValue("firstName", name[0]);
                        validation.setFieldValue("lastName", last_name);

                    } else {
                        validation.setFieldValue("entityName", full_name);
                    }

                    if (category == "Company") {
                        validation.setFieldValue(
                            "entityConstitution",
                            "Private Limited Company"
                        );
                    } else if (category == "Firm") {
                        validation.setFieldValue("entityConstitution", "Partnership");
                    } else if (category == "Individual") {
                        validation.setFieldValue("entityConstitution", "Proprietorship");
                    }
                } else {
                    setToastInfo({
                        message: "Incorrect PAN",
                        showToast: true,
                        role: "error",
                    });
                    validation.setFieldValue("entityName", "");
                    validation.setFieldValue("entityConstitution", "");
                }
            })
            .catch((e) => {
                setToastInfo({
                    message: "PAN not found",
                    showToast: true,
                    role: "error",
                });
                validation.setFieldValue("entityName", "");
                validation.setFieldValue("entityConstitution", "");
            })
            .finally((f) => {
                setLoader(false);
            });
    };

    useEffect(() => {
        callGetState()
            .then((response) => {
                if (response.code == "200") {
                    setStateList(response.data);
                }
            })
            .catch((error) => { });

        getRoleByEntityType({ searchKey: 'Partner' })
            .then(responseFromAPI => {
                if (responseFromAPI.status && responseFromAPI.code == 200) {
                    setRoleList(responseFromAPI.data.data)
                }
            })
            .catch(error => { })
    }, []);

    const handleCancel = () => {
        navigate(-1)
    };

    function setStateValue(data, state) {
        if (state) {
            for (const item of data) {
                if (item.name === state) {
                    const value = { label: `${item.name}`, value: state };
                    return value;
                }
            }
        }
    }

    function setCityValue(data, city) {
        if (city) {
            for (const item of data) {
                if (item.cityName === city) {
                    const value = { label: `${item.cityName}`, value: city };
                    return value;
                }
            }
        } else {
            return null
        }
    }

    const handleInputChange = (value) => {
        validation.setFieldValue("state", value);
        validation.setFieldValue("city", "")
        getCityList(value)
    };

    const handleCityChange = (value) => {
        validation.setFieldValue("city", value);
    };

    const getCityList = (value) => {
        callGetCity({ state: value })
            .then((response) => {
                if (response.status) {
                    setCityList(response?.data)
                }
            })
            .catch((error) => { })
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Add Partner" />
                    <Row className="vertical-scroll">
                        <Col xs="12">
                            <div>
                                <div>
                                    <Form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            validation.handleSubmit();
                                            return false;
                                        }}
                                    >
                                        <div className="mb-3 row">
                                            <Col sm="12" md="6">
                                                <Label className="form-label ">
                                                    Partner Type*
                                                </Label>
                                                <Input
                                                    name="partnerType"
                                                    type="select"
                                                    onChange={(e) => {
                                                        validation.resetForm()
                                                        validation.handleChange(e);
                                                        if (e.target.value === "Individual") {
                                                            setIsOpen(true)
                                                        } else {
                                                            setIsOpen(false)
                                                        }
                                                        setPartnerType(e.target.value)
                                                    }}
                                                    onBlur={validation.handleBlur}
                                                    value={validation.values.partnerType || ""}
                                                    invalid={
                                                        validation.touched.partnerType &&
                                                        validation.errors.partnerType
                                                    }
                                                >
                                                    <option value="" disabled>
                                                        Select Partner Type
                                                    </option>
                                                    <option value="Entity">Entity</option>
                                                    <option value="Individual">Individual</option>
                                                </Input>
                                                {validation.touched.partnerType &&
                                                    validation.errors.partnerType ? (
                                                    <FormFeedback type="invalid">
                                                        {validation.errors.partnerType}
                                                    </FormFeedback>
                                                ) : null}
                                            </Col>

                                            {
                                                partnerType === "Entity" ?
                                                    <>
                                                        <Col sm="12" md="6">
                                                            <Label className="form-label">GSTIN</Label>
                                                            <Input
                                                                name="gstnIn"
                                                                type="text"
                                                                placeholder="Enter GSTIN"
                                                                onChange={(e) => {
                                                                    validation.handleChange(e);
                                                                    onGSTINChange(e.target.value);
                                                                }}
                                                                onBlur={validation.handleBlur}
                                                                value={validation.values.gstnIn || ""}
                                                                invalid={
                                                                    validation.touched.gstnIn &&
                                                                    validation.errors.gstnIn
                                                                }
                                                            />
                                                            {validation.touched.gstnIn &&
                                                                validation.errors.gstnIn ? (
                                                                <FormFeedback type="invalid">
                                                                    {validation.errors.gstnIn}
                                                                </FormFeedback>
                                                            ) : null}
                                                        </Col>

                                                        <Col sm="12" md="6">
                                                            <Label className="form-label mt-4">PAN*</Label>
                                                            <Input
                                                                name="pan"
                                                                type="text"
                                                                placeholder="Enter PAN"
                                                                onChange={(e) => {
                                                                    validation.handleChange(e);
                                                                    onPanChange(e.target.value);
                                                                }}
                                                                onBlur={validation.handleBlur}
                                                                value={validation.values.pan || ""}
                                                                invalid={
                                                                    validation.touched.pan && validation.errors.pan
                                                                }
                                                            />
                                                            {validation.touched.pan && validation.errors.pan ? (
                                                                <FormFeedback type="invalid">
                                                                    {validation.errors.pan}
                                                                </FormFeedback>
                                                            ) : null}
                                                        </Col>

                                                        <Col sm="12" md="6">
                                                            <Label className="form-label mt-4">Entity Name*</Label>
                                                            <Input
                                                                name="entityName"
                                                                type="text"
                                                                placeholder="Enter Entity Name"
                                                                onChange={validation.handleChange}
                                                                onBlur={validation.handleBlur}
                                                                value={validation.values.entityName || ""}
                                                                invalid={
                                                                    validation.touched.entityName &&
                                                                    validation.errors.entityName
                                                                }
                                                            />
                                                            {validation.touched.entityName &&
                                                                validation.errors.entityName ? (
                                                                <FormFeedback type="invalid">
                                                                    {validation.errors.entityName}
                                                                </FormFeedback>
                                                            ) : null}
                                                        </Col>

                                                        <Col sm="12" md="6">
                                                            <Label className="form-label mt-4">
                                                                Date of Incorporation*
                                                            </Label>
                                                            <Input
                                                                name="dateOfIncorporation"
                                                                type="date"
                                                                onChange={validation.handleChange}
                                                                onBlur={validation.handleBlur}
                                                                value={validation.values.dateOfIncorporation || ""}
                                                                invalid={
                                                                    validation.touched.dateOfIncorporation &&
                                                                    validation.errors.dateOfIncorporation
                                                                }
                                                                max={currentDate}
                                                            />
                                                            {validation.touched.dateOfIncorporation &&
                                                                validation.errors.dateOfIncorporation ? (
                                                                <FormFeedback type="invalid">
                                                                    {validation.errors.dateOfIncorporation}
                                                                </FormFeedback>
                                                            ) : null}
                                                        </Col>

                                                        <Col sm="12" md="6">
                                                            <Label className="form-label mt-4">
                                                                Entity Constitution*
                                                            </Label>
                                                            <Input
                                                                name="entityConstitution"
                                                                type="select"
                                                                placeholder="Enter Entity constitution"
                                                                onChange={(e) => {
                                                                    validation.handleChange(e);
                                                                    if (e.target.value == 'Private Limited Company' || e.target.value == 'Public Limited Company') {
                                                                        setIsConstitution(true)
                                                                    } else {
                                                                        setIsConstitution(false)
                                                                    }
                                                                }}
                                                                onBlur={validation.handleBlur}
                                                                value={validation.values.entityConstitution || ""}
                                                                invalid={
                                                                    validation.touched.entityConstitution &&
                                                                    validation.errors.entityConstitution
                                                                }
                                                            >
                                                                <option value="" disabled>
                                                                    Select Entity Constitution
                                                                </option>
                                                                <option value="Private Limited Company">
                                                                    Private Limited Company
                                                                </option>
                                                                <option value="Limited Liability Partnership">
                                                                    Limited Liability Partnership
                                                                </option>
                                                                <option value="Proprietorship">Proprietorship</option>
                                                                <option value="Partnership">Partnership</option>
                                                                <option value="Public Limited Company">
                                                                    Public Limited Company
                                                                </option>
                                                            </Input>
                                                            {validation.touched.entityConstitution &&
                                                                validation.errors.entityConstitution ? (
                                                                <FormFeedback type="invalid">
                                                                    {validation.errors.entityConstitution}
                                                                </FormFeedback>
                                                            ) : null}
                                                        </Col>

                                                        <Col sm="12" md="6">
                                                            <Label className="form-label mt-4">
                                                                CIN
                                                                {validation.values.entityConstitution ==
                                                                    "Private Limited Company" ||
                                                                    validation.values.entityConstitution ==
                                                                    "Public Limited Company"
                                                                    ? "*"
                                                                    : null}
                                                            </Label>
                                                            <Input
                                                                name="cin"
                                                                type="text"
                                                                placeholder="Enter CIN"
                                                                onChange={validation.handleChange}
                                                                onBlur={validation.handleBlur}
                                                                value={validation.values.cin || ""}
                                                                invalid={
                                                                    validation.touched.cin && validation.errors.cin
                                                                }
                                                            />
                                                            {validation.touched.cin && validation.errors.cin ? (
                                                                <FormFeedback type="invalid">
                                                                    {validation.errors.cin}
                                                                </FormFeedback>
                                                            ) : null}
                                                        </Col>

                                                        <Col sm="12" md="6">
                                                            <Label className="form-label mt-4">
                                                                Company Description
                                                            </Label>
                                                            <Input
                                                                name="companyDescription"
                                                                type="textarea"
                                                                placeholder="Company Description"
                                                                onChange={validation.handleChange}
                                                                onBlur={validation.handleBlur}
                                                                value={validation.values.companyDescription || ""}
                                                            />
                                                        </Col>

                                                    </>
                                                    : null
                                            }
                                        </div>

                                        {
                                            partnerType !== "" ? <>
                                                <div
                                                    style={{
                                                        marginTop: "3%",
                                                        borderTop: "1px solid #f0f0f0",
                                                    }}
                                                >
                                                    <h5 style={{ marginTop: "1%" }}>Address Details</h5>
                                                </div>

                                                <div className="mb-3 row">
                                                    <Col sm="12" md="6">
                                                        <Label className="form-label mt-4">Address Type*</Label>
                                                        <Input
                                                            name="addressType"
                                                            type="select"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.addressType || ""}
                                                            invalid={
                                                                validation.touched.addressType &&
                                                                validation.errors.addressType
                                                            }
                                                        >
                                                            <option value="" disabled>
                                                                Select address type
                                                            </option>
                                                            <option value="registered_business_address">
                                                                Registered Business Address
                                                            </option>
                                                            <option value="head_office_address">
                                                                Head Office Address
                                                            </option>
                                                        </Input>
                                                        {validation.touched.addressType &&
                                                            validation.errors.addressType ? (
                                                            <FormFeedback type="invalid">
                                                                {validation.errors.addressType}
                                                            </FormFeedback>
                                                        ) : null}
                                                    </Col>

                                                    <Col sm="12" md="6">
                                                        <Label className="form-label mt-4">
                                                            Address Line1*
                                                        </Label>
                                                        <Input
                                                            name="address1"
                                                            type="text"
                                                            placeholder="Enter Address Line1"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.address1 || ""}
                                                            invalid={
                                                                validation.touched.address1 &&
                                                                validation.errors.address1
                                                            }
                                                        />
                                                        {validation.touched.address1 &&
                                                            validation.errors.address1 ? (
                                                            <FormFeedback type="invalid">
                                                                {validation.errors.address1}
                                                            </FormFeedback>
                                                        ) : null}
                                                    </Col>

                                                    <Col sm="12" md="6">
                                                        <Label className="form-label mt-4">Address Line2</Label>
                                                        <Input
                                                            name="address2"
                                                            type="text"
                                                            placeholder="Enter Address Line2"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.address2 || ""}
                                                            invalid={
                                                                validation.touched.address2 &&
                                                                validation.errors.address2
                                                            }
                                                        />
                                                        {validation.touched.address2 &&
                                                            validation.errors.address2 ? (
                                                            <FormFeedback type="invalid">
                                                                {validation.errors.address2}
                                                            </FormFeedback>
                                                        ) : null}
                                                    </Col>

                                                    <Col sm="12" md="6">
                                                        <Label className="form-label mt-4">Address Line3</Label>
                                                        <Input
                                                            name="address3"
                                                            type="text"
                                                            placeholder="Enter Address Line3"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.address3 || ""}
                                                            invalid={
                                                                validation.touched.address3 &&
                                                                validation.errors.address3
                                                            }
                                                        />
                                                        {validation.touched.address3 &&
                                                            validation.errors.address3 ? (
                                                            <FormFeedback type="invalid">
                                                                {validation.errors.address3}
                                                            </FormFeedback>
                                                        ) : null}
                                                    </Col>

                                                    <Col sm="12" md="6">
                                                        <Label className="form-label mt-4">State*</Label>
                                                        <Select
                                                            options={stateList.map((option) => ({
                                                                label: option.name,
                                                                value: option.name,
                                                            }))}
                                                            className={`select-dropdown ${validation.touched.state && validation.errors.state
                                                                ? "is-invalid"
                                                                : ""
                                                                }`}
                                                            name="state"
                                                            id="state"
                                                            onChange={(selectedOption) => {
                                                                handleInputChange(selectedOption.value);
                                                            }}
                                                            value={setStateValue(
                                                                stateList,
                                                                validation.values.state
                                                            )}
                                                        />
                                                        {validation.touched.state && validation.errors.state ? (
                                                            <FormFeedback type="invalid">
                                                                {validation.errors.state}
                                                            </FormFeedback>
                                                        ) : null}
                                                    </Col>

                                                    <Col sm="12" md="6">
                                                        <Label className="form-label mt-4">City*</Label>
                                                        <Select
                                                            options={cityList.map((option) => ({
                                                                label: option.cityName,
                                                                value: option.cityName,
                                                            }))}
                                                            className={`select-dropdown ${validation.touched.city && validation.errors.city
                                                                ? "is-invalid"
                                                                : ""
                                                                }`}
                                                            name="city"
                                                            id="city"
                                                            onChange={(selectedOption) => {
                                                                handleCityChange(selectedOption.value);
                                                            }}
                                                            value={setCityValue(
                                                                cityList,
                                                                validation.values.city
                                                            )}
                                                        />
                                                        {validation.touched.city && validation.errors.city ? (
                                                            <FormFeedback type="invalid">
                                                                {validation.errors.city}
                                                            </FormFeedback>
                                                        ) : null}
                                                    </Col>

                                                    <Col sm="12" md="6">
                                                        <Label className="form-label mt-4">Pincode*</Label>
                                                        <Input
                                                            name="pincode"
                                                            type="text"
                                                            placeholder="Enter Pincode"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            value={validation.values.pincode || ""}
                                                            invalid={
                                                                validation.touched.pincode &&
                                                                validation.errors.pincode
                                                            }
                                                        />
                                                        {validation.touched.pincode &&
                                                            validation.errors.pincode ? (
                                                            <FormFeedback type="invalid">
                                                                {validation.errors.pincode}
                                                            </FormFeedback>
                                                        ) : null}
                                                    </Col>
                                                </div>

                                                <div
                                                    style={{
                                                        marginTop: "3%",
                                                        borderTop: "1px solid #f0f0f0",
                                                    }}
                                                >
                                                    <h5
                                                        style={{ marginTop: "1%", cursor: "pointer" }}
                                                        onClick={handlClick}
                                                    >
                                                        {partnerType === "Entity" && "+ "}Add User
                                                    </h5>
                                                </div>
                                            </>
                                                : null
                                        }


                                        {isOpen && (
                                            <div className="mb-3 row">
                                                {
                                                    partnerType == "Individual" ?
                                                        <Col sm="12" md="6">
                                                            <Label className="form-label mt-4">PAN*</Label>
                                                            <Input
                                                                name="pan"
                                                                type="text"
                                                                placeholder="Enter PAN"
                                                                onChange={(e) => {
                                                                    validation.handleChange(e);
                                                                    onPanChange(e.target.value);
                                                                }}
                                                                onBlur={validation.handleBlur}
                                                                value={validation.values.pan || ""}
                                                                invalid={
                                                                    validation.touched.pan && validation.errors.pan
                                                                }
                                                            />
                                                            {validation.touched.pan && validation.errors.pan ? (
                                                                <FormFeedback type="invalid">
                                                                    {validation.errors.pan}
                                                                </FormFeedback>
                                                            ) : null}
                                                        </Col>
                                                        : null
                                                }
                                                <Col sm="12" md="6">
                                                    <Label className="form-label mt-4">First Name*</Label>
                                                    <Input
                                                        name="firstName"
                                                        type="text"
                                                        placeholder="Enter First Name"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.firstName || ""}
                                                        invalid={
                                                            validation.touched.firstName &&
                                                            validation.errors.firstName
                                                        }
                                                    />
                                                    {validation.touched.firstName &&
                                                        validation.errors.firstName ? (
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.firstName}
                                                        </FormFeedback>
                                                    ) : null}
                                                </Col>

                                                <Col sm="12" md="6">
                                                    <Label className="form-label mt-4">Last Name*</Label>
                                                    <Input
                                                        name="lastName"
                                                        type="text"
                                                        placeholder="Enter Last Name"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.lastName || ""}
                                                        invalid={
                                                            validation.touched.lastName &&
                                                            validation.errors.lastName
                                                        }
                                                    />
                                                    {validation.touched.lastName &&
                                                        validation.errors.lastName ? (
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.lastName}
                                                        </FormFeedback>
                                                    ) : null}
                                                </Col>

                                                <Col sm="12" md="6">
                                                    <Label className="form-label mt-4">Email*</Label>
                                                    <Input
                                                        name="email"
                                                        type="email"
                                                        placeholder="Enter Email"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.email || ""}
                                                        invalid={
                                                            validation.touched.email &&
                                                            validation.errors.email
                                                        }
                                                    />
                                                    {validation.touched.email &&
                                                        validation.errors.email ? (
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.email}
                                                        </FormFeedback>
                                                    ) : null}
                                                </Col>

                                                <Col sm="12" md="6">
                                                    <Label className="form-label mt-4">Mobile No.*</Label>
                                                    <Input
                                                        name="mobileNo"
                                                        type="number"
                                                        placeholder="Enter Mobile No"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.mobileNo || ""}
                                                        invalid={
                                                            validation.touched.mobileNo &&
                                                            validation.errors.mobileNo
                                                        }
                                                    />
                                                    {validation.touched.mobileNo &&
                                                        validation.errors.mobileNo ? (
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.mobileNo}
                                                        </FormFeedback>
                                                    ) : null}
                                                </Col>

                                                <Col sm="12" md="6">
                                                    <Label className="form-label mt-4">Role Type*</Label>
                                                    <Input
                                                        name="roleId"
                                                        type="select"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.roleId || ""}
                                                        invalid={validation.touched.roleId && validation.errors.roleId}
                                                    >
                                                        <option value="" disabled>Select Role Type</option>
                                                        {roleList.map((option) => (
                                                            <option key={option.id} value={option.id}>
                                                                {option.displayName}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                    {validation.touched.roleId && validation.errors.roleId ? (
                                                        <FormFeedback type="invalid">{validation.errors.roleId}</FormFeedback>
                                                    ) : null}
                                                </Col>
                                            </div>
                                        )}

                                        <Row>
                                            {!isAlready && (
                                                <Col>
                                                    <div className="d-flex gap-3 justify-content-end">
                                                        <button
                                                            className="btn btn-outline-secondary"
                                                            type="button"
                                                            onClick={handleCancel}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="submit"
                                                            className="btn btn-success save-customer"
                                                        >
                                                            Save
                                                        </button>
                                                    </div>
                                                </Col>
                                            )}
                                        </Row>

                                    </Form>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
            <ToastDialog
                message={toastInfo.message || ""}
                showToast={toastInfo.showToast}
                role={toastInfo.role}
                onCloseFn={() => setToastInfo({ showToast: false })}
            />
            <Loader show={loader} type="box" message="Fetching Data" />
            <Loader show={saveLoader} type="box" message="Saving Entity Details" />
        </React.Fragment>
    );
};

export default withRouter(AddPartner);

AddPartner.propTypes = {
    history: PropTypes.object,
};
